import { Head, router, useForm, usePage } from "@inertiajs/react";
import { ChevronDown, Clipboard, HelpCircle, Loader2, PlusCircle, Upload, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import AppLayout from "@/layouts/app-layout";
import { dashboardPath, socialImagePath } from "@/routes";
import type { BreadcrumbItem } from "@/types";

const POLL_INTERVAL = 4000;
const GENERATING_STATUSES = new Set([
  "pending",
  "processing",
  "screenshot",
  "metadata",
  "analyzing",
  "generating",
  "finalizing",
]);

interface Variation {
  id: string;
  status: string;
  active: boolean;
  created_at?: string | null;
  style: string;
  style_label: string;
  custom_prompt?: string | null;
  title?: string | null;
  image_url?: string | null;
}

interface SocialImageDataBase {
  id: string;
  url: string;
  title?: string | null;
  description?: string | null;
  has_custom_logo: boolean;
  background_color?: string | null;
  text_color?: string | null;
  contrast_ratio?: number | null;
  customer_email?: string | null;
  active_social_image_id?: string | null;
  active_image_url?: string | null;
  variations: Variation[];
}

type SocialImageData = SocialImageDataBase &
  (
    | {
        page_type: "ecommerce_product";
        unique_selling_point: string;
      }
    | {
        page_type: "podcast_episode";
        episode_date: string;
      }
    | {
        page_type: "event_page";
        event_date: string | null;
        event_location: string | null;
      }
    | {
        page_type: "blog_post";
        blog_author: string;
        blog_date: string;
      }
    | {
        page_type: "book";
        book_author: string;
        book_series_number: number;
      }
    | { page_type: string }
  );

interface SupportedStyle {
  value: string;
  label: string;
}

interface SocialImageShowProps {
  social_image: SocialImageData;
  supported_styles?: SupportedStyle[];
  max_variations: number;
  default_variation_count: number;
}

interface PlaceholderItem {
  placeholder: true;
  key: string;
  shouldShowLoading: boolean;
}

type GridItem = Variation | PlaceholderItem;

const formatStatus = (status: string) =>
  status
    .split("_")
    .map((segment) => (segment.length > 0 ? `${segment[0].toUpperCase()}${segment.slice(1)}` : segment))
    .join(" ");

export default function SocialImageShow({
  social_image,
  supported_styles = [],
  max_variations,
  default_variation_count,
}: SocialImageShowProps) {
  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [
      {
        title: "Dashboard",
        href: dashboardPath(),
      },
    ];

    items.push({
      title: social_image.url,
      href: socialImagePath(social_image.id),
    });

    return items;
  }, [social_image.id, social_image.title, social_image.url]);

  const baseImageId = social_image.active_social_image_id ?? social_image.id;

  const defaultStyleValue = useMemo(() => {
    if (supported_styles?.some((style) => style.value === "default")) {
      return "default";
    }
    return supported_styles?.[0]?.value ?? "default";
  }, [supported_styles]);

  const initialCount = useMemo(() => {
    const clamped = Math.min(default_variation_count, max_variations);
    return clamped > 0 ? clamped : 1;
  }, [default_variation_count, max_variations]);

  const { credits, trial } = usePage().props;
  const isTrial = trial?.trialing;
  const availableAiCredits = credits?.available ?? 0;
  const maxAiVariations = Math.min(max_variations, availableAiCredits);
  const aiCountCeiling = Math.max(1, maxAiVariations);
  const hasAnyCredits = availableAiCredits > 0 && !isTrial;
  const creditResetLabel = useMemo(() => {
    if (!credits?.plan?.reset_at) return null;
    const date = new Date(credits.plan.reset_at);
    if (Number.isNaN(date.getTime())) return null;
    return `Resets ${date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    })}`;
  }, [credits?.plan?.reset_at]);

  // Extract page-specific values based on page_type
  const getPageSpecificDefaults = useCallback(() => {
    const pageType = social_image.page_type;
    const defaults: Record<string, string | number | null> = {};

    if (pageType === "ecommerce_product" && "unique_selling_point" in social_image) {
      defaults.unique_selling_point = social_image.unique_selling_point ?? "";
    } else if (pageType === "podcast_episode" && "episode_date" in social_image) {
      defaults.episode_date = social_image.episode_date ?? "";
    } else if (pageType === "event_page" && "event_date" in social_image) {
      defaults.event_date = social_image.event_date ?? "";
      defaults.event_location = social_image.event_location ?? "";
    } else if (pageType === "blog_post" && "blog_author" in social_image) {
      defaults.blog_author = social_image.blog_author ?? "";
      defaults.blog_date = social_image.blog_date ?? "";
    } else if (pageType === "book" && "book_author" in social_image) {
      defaults.book_author = social_image.book_author ?? "";
      defaults.book_series_number = social_image.book_series_number ?? null;
    }

    return defaults;
  }, [social_image]);

  const pageSpecificDefaults = getPageSpecificDefaults();

  const {
    data: templateFormData,
    setData: setTemplateFormData,
    put: putTemplateFormData,
    processing: processingTemplateForm,
  } = useForm({
    title: social_image.title ?? "",
    description: social_image.description ?? "",
    background_color: social_image.background_color ?? "#FFFFFF",
    text_color: social_image.text_color ?? "#000000",
    custom_image: null as File | null,
    custom_image_url: "",
    custom_image_name: "",
    custom_image_preview: null as string | null,
    custom_logo: null as File | null,
    custom_logo_url: "",
    custom_logo_name: "",
    custom_logo_preview: null as string | null,
    base_social_image_id: baseImageId,
    // Page-specific fields
    unique_selling_point: (pageSpecificDefaults.unique_selling_point as string) ?? "",
    episode_date: (pageSpecificDefaults.episode_date as string) ?? "",
    event_date: (pageSpecificDefaults.event_date as string) ?? "",
    event_location: (pageSpecificDefaults.event_location as string) ?? "",
    blog_author: (pageSpecificDefaults.blog_author as string) ?? "",
    blog_date: (pageSpecificDefaults.blog_date as string) ?? "",
    book_author: (pageSpecificDefaults.book_author as string) ?? "",
    book_series_number: pageSpecificDefaults.book_series_number as number | null,
  });

  const {
    data: aiImageFormData,
    setData: setAiImageFormData,
    post: postAiImageFormData,
    processing: processingAiImageFormData,
    errors: aiImageFormErrors,
  } = useForm({
    base_social_image_id: baseImageId,
    mode: "ai" as "default" | "ai",
    style: defaultStyleValue,
    custom_prompt: "",
    count: Math.min(initialCount, aiCountCeiling),
    include_screenshot: true,
  });

  const [mode, setMode] = useState<"default" | "ai">("default");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [markingActiveId, setMarkingActiveId] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  // Collapsible section state (accordion behavior - only one open at a time)
  const [openSection, setOpenSection] = useState<"textColors" | "logo" | "image" | null>("textColors");

  useEffect(() => {
    if (aiImageFormData.count > aiCountCeiling) {
      setAiImageFormData("count", aiCountCeiling);
    }
  }, [aiCountCeiling, aiImageFormData.count, setAiImageFormData]);

  const hasPendingVariations = useMemo(
    () => social_image.variations.some((variation) => GENERATING_STATUSES.has(variation.status)),
    [social_image.variations],
  );

  const generatingVariationsCount = useMemo(
    () => social_image.variations.filter((variation) => GENERATING_STATUSES.has(variation.status)).length,
    [social_image.variations],
  );

  useEffect(() => {
    if (!hasPendingVariations) {
      if (pollRef.current !== null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    if (pollRef.current !== null) return;

    pollRef.current = window.setInterval(() => {
      router.reload({ only: ["social_image"], preserveUrl: true });
    }, POLL_INTERVAL);

    return () => {
      if (pollRef.current !== null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [hasPendingVariations]);

  useEffect(() => {
    return () => {
      if (pollRef.current !== null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  const handleTopup = () => {
    router.post("/credits/topup", {}, { preserveScroll: true });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isTrial) {
      router.visit("/subscribe");
      return;
    }
    if (!hasAnyCredits) {
      handleTopup();
      return;
    }
    if (requestedExceedsCredits) return;
    postAiImageFormData("/social_images", {
      preserveScroll: true,
      onStart: () => setIsSubmitting(true),
      onFinish: () => setIsSubmitting(false),
    });
  };

  const handleImageUpdate = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!templateFormData.title.trim()) return;

    putTemplateFormData(socialImagePath(social_image.id), {
      preserveScroll: true,
      onStart: () => setIsSubmitting(true),
      onFinish: () => setIsSubmitting(false),
    });
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setTemplateFormData("custom_image", file);
      setTemplateFormData("custom_image_name", file.name);
      setTemplateFormData("custom_image_url", ""); // Clear URL if file is uploaded
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setTemplateFormData("custom_image_preview", reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setTemplateFormData("custom_image", null);
    setTemplateFormData("custom_image_name", "");
    setTemplateFormData("custom_image_preview", null);
    setTemplateFormData("custom_image_url", "");
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setTemplateFormData("custom_logo", file);
      setTemplateFormData("custom_logo_name", file.name);
      setTemplateFormData("custom_logo_url", ""); // Clear URL if file is uploaded
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setTemplateFormData("custom_logo_preview", reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setTemplateFormData("custom_logo", null);
    setTemplateFormData("custom_logo_name", "");
    setTemplateFormData("custom_logo_preview", null);
    setTemplateFormData("custom_logo_url", "");
  };

  const handleImagePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith("image/")) {
          event.preventDefault();
          const file = item.getAsFile();
          if (file) {
            setTemplateFormData("custom_image", file);
            setTemplateFormData("custom_image_name", `pasted-image.${file.type.split("/")[1] || "png"}`);
            setTemplateFormData("custom_image_url", "");
            const reader = new FileReader();
            reader.onloadend = () => {
              setTemplateFormData("custom_image_preview", reader.result as string);
            };
            reader.readAsDataURL(file);
          }
          break;
        }
      }
    },
    [setTemplateFormData],
  );

  const handleLogoPaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith("image/")) {
          event.preventDefault();
          const file = item.getAsFile();
          if (file) {
            setTemplateFormData("custom_logo", file);
            setTemplateFormData("custom_logo_name", `pasted-logo.${file.type.split("/")[1] || "png"}`);
            setTemplateFormData("custom_logo_url", "");
            const reader = new FileReader();
            reader.onloadend = () => {
              setTemplateFormData("custom_logo_preview", reader.result as string);
            };
            reader.readAsDataURL(file);
          }
          break;
        }
      }
    },
    [setTemplateFormData],
  );

  const variations = social_image.variations;
  const placeholdersCount = Math.max(0, max_variations - variations.length);

  // Determine how many placeholders should show loading
  // Only show loading placeholders for requested generations that
  // do not yet have a corresponding variation record. This prevents
  // duplicate loading states when a variation is already present and
  // still generating.
  const requestedLoadingSlots =
    isSubmitting || processingAiImageFormData ? aiImageFormData.count : generatingVariationsCount;

  const loadingPlaceholderCount = Math.max(
    0,
    Math.min(requestedLoadingSlots - generatingVariationsCount, placeholdersCount),
  );

  const placeholderItems: PlaceholderItem[] = Array.from({ length: placeholdersCount }, (_value, index) => ({
    placeholder: true,
    key: `placeholder-${index}`,
    shouldShowLoading: index < loadingPlaceholderCount,
  }));
  const gridItems: GridItem[] = [...variations, ...placeholderItems];

  const handleMarkActive = (id: string) => {
    setMarkingActiveId(id);
    router.put(
      socialImagePath(id),
      { active: true },
      {
        preserveScroll: true,
        onSuccess: () => {
          router.reload({ only: ["social_image"], preserveUrl: true });
        },
        onFinish: () => {
          setMarkingActiveId(null);
        },
      },
    );
  };

  const pageTitle = social_image.title ? `${social_image.title} · Social Images` : "Social Images";

  const isImageDirty =
    templateFormData.title.trim() !== (social_image.title ?? "").trim() ||
    templateFormData.description.trim() !== (social_image.description ?? "").trim() ||
    templateFormData.background_color !== (social_image.background_color ?? "#FFFFFF") ||
    templateFormData.text_color !== (social_image.text_color ?? "#000000") ||
    templateFormData.custom_image !== null ||
    templateFormData.custom_logo !== null ||
    templateFormData.custom_logo_url.trim() !== "";
  const isTitleValid = templateFormData.title.trim().length > 0;
  const requestedExceedsCredits = hasAnyCredits && aiImageFormData.count > availableAiCredits;

  const renderGridItem = (item: GridItem) => {
    if ("placeholder" in item) {
      return (
        <div key={item.key} className="bg-muted/10 relative overflow-hidden rounded-lg border border-dashed">
          <Skeleton className={`aspect-[1.91/1] w-full ${item.shouldShowLoading ? "" : "!animate-none"}`} />
          {item.shouldShowLoading && (
            <div className="bg-background/80 absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
              <Loader2 className="text-primary h-6 w-6 animate-spin" />
              <p className="text-muted-foreground text-xs font-medium">This usually takes 20-30 seconds.</p>
            </div>
          )}
        </div>
      );
    }

    const isGenerating = GENERATING_STATUSES.has(item.status);
    const isFailed = item.status === "failed";
    const hasNoImage = !item.image_url;

    // Show loading state only if actively generating
    if (isGenerating) {
      return (
        <div key={item.id} className="space-y-2">
          <div className="bg-muted/20 relative overflow-hidden rounded-lg border">
            <Skeleton className="aspect-[1.91/1] w-full" />
            <div className="bg-background/80 absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
              <Loader2 className="text-primary h-6 w-6 animate-spin" />
              <p className="text-muted-foreground text-xs font-medium">This usually takes 20-30 seconds.</p>
              <p className="text-muted-foreground text-[11px] tracking-wide uppercase">{formatStatus(item.status)}</p>
            </div>
          </div>
          <div className="text-muted-foreground flex items-center justify-between text-xs">
            <span>{item.style_label}</span>
            <span>{formatStatus(item.status)}</span>
          </div>
        </div>
      );
    }

    // Show failed state without loading spinner
    if (isFailed || (hasNoImage && item.status === "completed")) {
      return (
        <div key={item.id} className="space-y-2">
          <div className="bg-muted/20 relative overflow-hidden rounded-lg border">
            <Skeleton className="aspect-[1.91/1] w-full !animate-none" />
            <div className="bg-background/80 absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="text-destructive text-sm font-medium">
                {isFailed ? "Generation failed" : "No image available"}
              </p>
              <p className="text-muted-foreground text-[11px] tracking-wide uppercase">{formatStatus(item.status)}</p>
            </div>
          </div>
          <div className="text-muted-foreground flex items-center justify-between text-xs">
            <span>{item.style_label}</span>
            <span>{formatStatus(item.status)}</span>
          </div>
        </div>
      );
    }

    return (
      <div key={item.id} className="space-y-2">
        <div className="group bg-muted/20 relative aspect-[1.91/1] overflow-hidden rounded-lg border">
          <img
            src={item.image_url ?? ""}
            alt={item.title || "Generated social preview image"}
            className="object-fit h-full w-full"
          />
          {item.active && (
            <span className="bg-primary text-primary-foreground absolute top-3 left-3 rounded-full px-2 py-1 text-xs font-medium">
              Active
            </span>
          )}
          {!item.active && (
            <div className="bg-background/80 absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center opacity-0 transition-opacity group-hover:opacity-100">
              <Button
                type="button"
                size="sm"
                onClick={() => handleMarkActive(item.id)}
                disabled={markingActiveId === item.id}
              >
                {markingActiveId === item.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Mark as active
              </Button>
              <p className="text-muted-foreground text-[11px]">Switch this image live for the URL</p>
            </div>
          )}
        </div>
        <div className="text-muted-foreground flex items-center justify-between text-xs">
          <span>{item.style_label}</span>
          <span>{formatStatus(item.status)}</span>
        </div>
      </div>
    );
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={pageTitle} />
      <div className="flex flex-1 flex-col gap-6 p-6">
        <div className="grid gap-8 lg:grid-cols-[1fr_2fr]">
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="space-y-1">
                <h1 className="text-2xl font-semibold tracking-tight">Your current social preview</h1>
                <p className="text-muted-foreground text-sm">
                  This is your current social preview image. You can change it by generating new variations.
                </p>
              </div>
              <div className="bg-muted/20 relative overflow-hidden rounded-lg border">
                {social_image.active_image_url ? (
                  <img src={social_image.active_image_url} alt="Current active social preview" className="w-full" />
                ) : (
                  <div className="flex aspect-[1.91/1] items-center justify-center">
                    <Skeleton className="h-full w-full" />
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-1">
                <h2 className="text-lg font-semibold">Generate new variations</h2>
                <p className="text-muted-foreground text-xs">Choose how you&apos;d like to create your variations</p>
              </div>

              <Tabs
                value={mode}
                onValueChange={(v) => {
                  const newMode = v as "default" | "ai";
                  setMode(newMode);
                  setAiImageFormData("mode", newMode);
                }}
              >
                <TabsList>
                  <TabsTrigger value="default">🎨 Default</TabsTrigger>
                  <TabsTrigger value="ai">🪄 Refine with AI</TabsTrigger>
                </TabsList>

                <TabsContent value="default" className="space-y-4">
                  <TooltipProvider>
                    <form onSubmit={handleImageUpdate} className="space-y-4">
                      <div className="space-y-3">
                        {/* 📝 Text & Colors Section */}
                        <Collapsible
                          open={openSection === "textColors"}
                          onOpenChange={(open) => setOpenSection(open ? "textColors" : null)}
                          className="rounded-lg border"
                        >
                          <CollapsibleTrigger className="hover:bg-muted/50 flex w-full items-center justify-between px-4 py-3 transition-colors">
                            <span className="text-sm font-medium">📝 Text & Colors</span>
                            <ChevronDown
                              className={`text-muted-foreground h-4 w-4 transition-transform duration-200 ${
                                openSection === "textColors" ? "rotate-180" : ""
                              }`}
                            />
                          </CollapsibleTrigger>
                          <CollapsibleContent className="border-t px-4 pt-3 pb-4">
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <Label htmlFor="template-title">Title</Label>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <HelpCircle className="text-muted-foreground h-4 w-4 cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-[200px] text-wrap">
                                      <p>
                                        The main heading displayed on your social preview. Keep it concise and
                                        compelling.
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                                <Input
                                  id="template-title"
                                  value={templateFormData.title}
                                  onChange={(event) => setTemplateFormData("title", event.target.value)}
                                  placeholder="Catchy headline that appears on the preview"
                                />
                              </div>

                              {social_image.page_type !== "ecommerce_product" &&
                                social_image.page_type !== "podcast_episode" &&
                                social_image.page_type !== "event_page" &&
                                social_image.page_type !== "blog_post" &&
                                social_image.page_type !== "book" && (
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                      <Label htmlFor="template-description">Description</Label>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <HelpCircle className="text-muted-foreground h-4 w-4 cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-[200px] text-pretty">
                                          <p>Secondary text that provides context. Up to 200 characters recommended.</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </div>
                                    <Textarea
                                      id="template-description"
                                      value={templateFormData.description}
                                      onChange={(event) => setTemplateFormData("description", event.target.value)}
                                      placeholder="Supporting text that appears below the title (optional)"
                                      rows={2}
                                      maxLength={200}
                                    />
                                    <p className="text-muted-foreground text-xs">
                                      {templateFormData.description.length}/200 characters
                                    </p>
                                  </div>
                                )}

                              {/* Page-specific fields */}
                              {social_image.page_type === "ecommerce_product" && (
                                <>
                                  <div className="space-y-2">
                                    <Label htmlFor="unique-selling-point">Unique Selling Point</Label>
                                    <Textarea
                                      id="unique-selling-point"
                                      value={templateFormData.unique_selling_point}
                                      onChange={(event) =>
                                        setTemplateFormData("unique_selling_point", event.target.value)
                                      }
                                      placeholder="What makes this product special?"
                                      rows={2}
                                    />
                                  </div>
                                </>
                              )}

                              {social_image.page_type === "podcast_episode" && (
                                <div className="space-y-2">
                                  <Label htmlFor="episode-date">Episode Date</Label>
                                  <Input
                                    id="episode-date"
                                    type="date"
                                    value={templateFormData.episode_date}
                                    onChange={(event) => setTemplateFormData("episode_date", event.target.value)}
                                  />
                                </div>
                              )}

                              {social_image.page_type === "event_page" && (
                                <>
                                  <div className="space-y-2">
                                    <Label htmlFor="event-date">Event Date</Label>
                                    <Input
                                      id="event-date"
                                      type="date"
                                      value={templateFormData.event_date}
                                      onChange={(event) => setTemplateFormData("event_date", event.target.value)}
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="event-location">Event Location</Label>
                                    <Input
                                      id="event-location"
                                      value={templateFormData.event_location}
                                      onChange={(event) => setTemplateFormData("event_location", event.target.value)}
                                      placeholder="City, Venue, or Online"
                                    />
                                  </div>
                                </>
                              )}

                              {social_image.page_type === "blog_post" && (
                                <>
                                  <div className="space-y-2">
                                    <Label htmlFor="blog-author">Author</Label>
                                    <Input
                                      id="blog-author"
                                      value={templateFormData.blog_author}
                                      onChange={(event) => setTemplateFormData("blog_author", event.target.value)}
                                      placeholder="Author name"
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="blog-date">Publish Date</Label>
                                    <Input
                                      id="blog-date"
                                      type="date"
                                      value={templateFormData.blog_date}
                                      onChange={(event) => setTemplateFormData("blog_date", event.target.value)}
                                    />
                                  </div>
                                </>
                              )}

                              {social_image.page_type === "book" && (
                                <>
                                  <div className="space-y-2">
                                    <Label htmlFor="book-author">Author</Label>
                                    <Input
                                      id="book-author"
                                      value={templateFormData.book_author}
                                      onChange={(event) => setTemplateFormData("book_author", event.target.value)}
                                      placeholder="Author name"
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="book-series-number">Series Number</Label>
                                    <Input
                                      id="book-series-number"
                                      type="number"
                                      min={0}
                                      value={templateFormData.book_series_number ?? ""}
                                      onChange={(event) => {
                                        const value = event.target.value;
                                        setTemplateFormData(
                                          "book_series_number",
                                          value === "" ? null : parseInt(value, 10),
                                        );
                                      }}
                                      placeholder="Book number in series"
                                    />
                                  </div>
                                </>
                              )}

                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <Label>Colors</Label>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <HelpCircle className="text-muted-foreground h-4 w-4 cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-[200px] text-pretty">
                                      <p>Background and text colors should have sufficient contrast for readability.</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                                <div className="flex gap-4">
                                  <div className="flex-1 space-y-2">
                                    <label htmlFor="bg-color" className="text-muted-foreground text-sm">
                                      Background
                                    </label>
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="color"
                                        id="bg-color"
                                        value={templateFormData.background_color}
                                        onChange={(event) =>
                                          setTemplateFormData("background_color", event.target.value)
                                        }
                                        className="border-border h-10 w-12 cursor-pointer rounded border"
                                      />
                                      <span className="text-muted-foreground font-mono text-sm">
                                        {templateFormData.background_color}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex-1 space-y-2">
                                    <label htmlFor="text-color" className="text-muted-foreground text-sm">
                                      Text
                                    </label>
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="color"
                                        id="text-color"
                                        value={templateFormData.text_color}
                                        onChange={(event) => setTemplateFormData("text_color", event.target.value)}
                                        className="border-border h-10 w-12 cursor-pointer rounded border"
                                      />
                                      <span className="text-muted-foreground font-mono text-sm">
                                        {templateFormData.text_color}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </CollapsibleContent>
                        </Collapsible>

                        {/* 🏷️ Logo Section */}
                        <Collapsible
                          open={openSection === "logo"}
                          onOpenChange={(open) => setOpenSection(open ? "logo" : null)}
                          className="rounded-lg border"
                        >
                          <CollapsibleTrigger className="hover:bg-muted/50 flex w-full items-center justify-between px-4 py-3 transition-colors">
                            <span className="text-sm font-medium">🏷️ Logo</span>
                            <ChevronDown
                              className={`text-muted-foreground h-4 w-4 transition-transform duration-200 ${
                                openSection === "logo" ? "rotate-180" : ""
                              }`}
                            />
                          </CollapsibleTrigger>
                          <CollapsibleContent className="border-t px-4 pt-3 pb-4">
                            <div className="space-y-2">
                              <p className="text-muted-foreground text-xs">
                                Update your social image&apos;s brand logo. Recommended: PNG with transparent
                                background.
                              </p>
                              <div className="space-y-3">
                                <div className="relative">
                                  <Input
                                    id="custom-logo-url"
                                    type="url"
                                    value={templateFormData.custom_logo_url}
                                    onChange={(event) => {
                                      setTemplateFormData("custom_logo_url", event.target.value);
                                      if (event.target.value.trim()) {
                                        setTemplateFormData("custom_logo", null);
                                        setTemplateFormData("custom_logo_name", "");
                                        setTemplateFormData("custom_logo_preview", null);
                                      }
                                    }}
                                    onPaste={handleLogoPaste}
                                    placeholder="Paste image or enter URL"
                                    className="pr-10"
                                  />
                                  <Clipboard className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2" />
                                </div>
                                <div className="relative">
                                  <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t" />
                                  </div>
                                  <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-background text-muted-foreground px-2">Or</span>
                                  </div>
                                </div>
                                <div className="border-border hover:border-primary/50 group cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors">
                                  <input
                                    type="file"
                                    onChange={handleLogoUpload}
                                    accept="image/*"
                                    className="hidden"
                                    id="custom-logo-upload"
                                  />
                                  <label htmlFor="custom-logo-upload" className="block cursor-pointer">
                                    <Upload className="text-muted-foreground group-hover:text-primary mx-auto mb-2 h-8 w-8 transition-colors" />
                                    <p className="text-foreground text-sm font-medium">
                                      {templateFormData.custom_logo_name
                                        ? templateFormData.custom_logo_name
                                        : "Click to upload"}
                                    </p>
                                    <p className="text-muted-foreground text-xs">PNG, JPG up to 10MB</p>
                                  </label>
                                </div>
                              </div>
                              {(templateFormData.custom_logo_name || templateFormData.custom_logo_url.trim()) && (
                                <div className="space-y-2">
                                  {templateFormData.custom_logo_preview && (
                                    <div className="relative aspect-square max-w-[200px] overflow-hidden rounded-lg border">
                                      <img
                                        src={templateFormData.custom_logo_preview}
                                        alt="Custom logo preview"
                                        className="h-full w-full object-contain"
                                      />
                                    </div>
                                  )}
                                  {templateFormData.custom_logo_url.trim() && !templateFormData.custom_logo_preview && (
                                    <div className="relative aspect-square max-w-[200px] overflow-hidden rounded-lg border">
                                      <img
                                        src={templateFormData.custom_logo_url.trim()}
                                        alt="Custom logo preview"
                                        className="h-full w-full object-contain"
                                        onError={(e) => {
                                          e.currentTarget.style.display = "none";
                                        }}
                                      />
                                    </div>
                                  )}
                                  <div className="bg-secondary/50 flex items-center justify-between gap-2 rounded-lg p-3">
                                    <span className="text-foreground truncate text-sm">
                                      {templateFormData.custom_logo_name || templateFormData.custom_logo_url.trim()}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={handleRemoveLogo}
                                      className="hover:bg-secondary rounded p-1 transition-colors"
                                    >
                                      <X className="text-muted-foreground h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>

                        {/* 🖼️ Custom Image Section */}
                        <Collapsible
                          open={openSection === "image"}
                          onOpenChange={(open) => setOpenSection(open ? "image" : null)}
                          className="rounded-lg border"
                        >
                          <CollapsibleTrigger className="hover:bg-muted/50 flex w-full items-center justify-between px-4 py-3 transition-colors">
                            <span className="text-sm font-medium">🖼️ Custom Image</span>
                            <ChevronDown
                              className={`text-muted-foreground h-4 w-4 transition-transform duration-200 ${
                                openSection === "image" ? "rotate-180" : ""
                              }`}
                            />
                          </CollapsibleTrigger>
                          <CollapsibleContent className="border-t px-4 pt-3 pb-4">
                            <div className="space-y-2">
                              <p className="text-muted-foreground text-xs">
                                Showcase a custom image on your social preview. Recommended size: 600x600px.
                              </p>
                              <div className="space-y-3">
                                <div className="relative">
                                  <Input
                                    id="custom-image-url"
                                    type="url"
                                    value={templateFormData.custom_image_url}
                                    onChange={(event) => {
                                      setTemplateFormData("custom_image_url", event.target.value);
                                      if (event.target.value.trim()) {
                                        setTemplateFormData("custom_image", null);
                                        setTemplateFormData("custom_image_name", "");
                                        setTemplateFormData("custom_image_preview", null);
                                      }
                                    }}
                                    onPaste={handleImagePaste}
                                    placeholder="Paste image or enter URL"
                                    className="pr-10"
                                  />
                                  <Clipboard className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2" />
                                </div>
                                <div className="relative">
                                  <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t" />
                                  </div>
                                  <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-background text-muted-foreground px-2">Or</span>
                                  </div>
                                </div>
                                <div className="border-border hover:border-primary/50 group cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors">
                                  <input
                                    type="file"
                                    onChange={handleImageUpload}
                                    accept="image/*"
                                    className="hidden"
                                    id="custom-image-upload"
                                  />
                                  <label htmlFor="custom-image-upload" className="block cursor-pointer">
                                    <Upload className="text-muted-foreground group-hover:text-primary mx-auto mb-2 h-8 w-8 transition-colors" />
                                    <p className="text-foreground text-sm font-medium">
                                      {templateFormData.custom_image_name
                                        ? templateFormData.custom_image_name
                                        : "Click to upload"}
                                    </p>
                                    <p className="text-muted-foreground text-xs">PNG, JPG up to 10MB</p>
                                  </label>
                                </div>
                              </div>
                              {(templateFormData.custom_image_name || templateFormData.custom_image_url.trim()) && (
                                <div className="space-y-2">
                                  {templateFormData.custom_image_preview && (
                                    <div className="relative aspect-[600/630] overflow-hidden rounded-lg border">
                                      <img
                                        src={templateFormData.custom_image_preview}
                                        alt="Custom image preview"
                                        className="h-full w-full object-cover"
                                      />
                                    </div>
                                  )}
                                  {templateFormData.custom_image_url.trim() &&
                                    !templateFormData.custom_image_preview && (
                                      <div className="relative aspect-[600/630] overflow-hidden rounded-lg border">
                                        <img
                                          src={templateFormData.custom_image_url.trim()}
                                          alt="Custom image preview"
                                          className="h-full w-full object-cover"
                                          onError={(e) => {
                                            e.currentTarget.style.display = "none";
                                          }}
                                        />
                                      </div>
                                    )}
                                  <div className="bg-secondary/50 flex items-center justify-between gap-2 rounded-lg p-3">
                                    <span className="text-foreground truncate text-sm">
                                      {templateFormData.custom_image_name || templateFormData.custom_image_url.trim()}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={handleRemoveImage}
                                      className="hover:bg-secondary rounded p-1 transition-colors"
                                    >
                                      <X className="text-muted-foreground h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      </div>

                      <p className="text-muted-foreground text-xs">
                        Changes here generate a new variation of the social preview with new text. You can activate it
                        once processing finishes.
                      </p>

                      <div className="flex items-center gap-2">
                        <Button
                          type="submit"
                          size="sm"
                          disabled={processingTemplateForm || !isImageDirty || !isTitleValid}
                        >
                          {processingTemplateForm && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Generate new preview
                        </Button>
                      </div>
                    </form>
                  </TooltipProvider>
                </TabsContent>

                <TabsContent value="ai" className="space-y-4">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="bg-muted/40 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-3 py-2 dark:border-neutral-700">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">Image credits</Badge>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">
                            {isTrial ? "Unavailable on trial" : `${availableAiCredits} left`}
                          </span>
                          {creditResetLabel && !isTrial ? (
                            <span className="text-muted-foreground text-xs">{creditResetLabel}</span>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!isTrial ? (
                          <Button type="button" variant="outline" size="sm" onClick={handleTopup}>
                            <PlusCircle className="mr-2 h-4 w-4" />
                            Top up
                          </Button>
                        ) : (
                          <Button type="button" variant="outline" size="sm" onClick={() => router.visit("/subscribe")}>
                            Choose a plan
                          </Button>
                        )}
                      </div>
                    </div>
                    {isTrial && (
                      <div className="rounded-lg border border-dashed border-amber-200 bg-amber-50/60 px-4 py-3 text-amber-900 dark:border-amber-500/60 dark:bg-amber-900/30 dark:text-amber-50">
                        <p className="font-semibold">AI refinements are disabled on trial.</p>
                        <p className="text-sm">
                          Pick a plan to unlock AI refinements, brand template generation, and new AI social images.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button type="button" onClick={() => router.visit("/subscribe")}>
                            Choose a plan
                          </Button>
                          <Button type="button" variant="ghost" onClick={() => setMode("default")}>
                            Keep editing manually
                          </Button>
                        </div>
                      </div>
                    )}
                    {!hasAnyCredits && !isTrial && (
                      <div className="rounded-lg border border-dashed border-amber-200 bg-amber-50/60 px-4 py-3 text-amber-900 dark:border-amber-500/60 dark:bg-amber-900/30 dark:text-amber-50">
                        <p className="font-semibold">You&apos;re out of image credits.</p>
                        <p className="text-sm">
                          Top up to unlock refinements, brand template generation, and new AI social images.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button type="button" onClick={handleTopup}>
                            Add credits
                          </Button>
                          <Button type="button" variant="ghost" onClick={() => setMode("default")}>
                            Keep editing manually
                          </Button>
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="style-select">Choose a style</Label>
                      <Select
                        value={aiImageFormData.style}
                        onValueChange={(value) => setAiImageFormData("style", value)}
                      >
                        <SelectTrigger id="style-select">
                          <SelectValue placeholder="Select a style" />
                        </SelectTrigger>
                        <SelectContent>
                          {supported_styles.map((style) => (
                            <SelectItem key={style.value} value={style.value}>
                              {style.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="custom-prompt">Custom prompt</Label>
                      <p className="text-muted-foreground text-xs">
                        Describe what you would like the new social preview to look like. Leave blank to rely on the
                        page data.
                      </p>
                      <Textarea
                        id="custom-prompt"
                        value={aiImageFormData.custom_prompt}
                        onChange={(event) => setAiImageFormData("custom_prompt", event.target.value)}
                        placeholder="Focus on the product hero, warm lighting, subtle gradients..."
                      />
                      {aiImageFormErrors.custom_prompt && (
                        <p className="text-destructive text-sm" role="alert">
                          {aiImageFormErrors.custom_prompt}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="variation-count">Number of images to generate</Label>
                      <Input
                        id="variation-count"
                        type="number"
                        min={1}
                        max={aiCountCeiling}
                        value={String(aiImageFormData.count)}
                        onChange={(event) => {
                          const raw = parseInt(event.target.value, 10);
                          if (Number.isNaN(raw)) {
                            setAiImageFormData("count", 1);
                            return;
                          }
                          const clamped = Math.min(Math.max(raw, 1), aiCountCeiling);
                          setAiImageFormData("count", clamped);
                        }}
                      />
                      <p className="text-muted-foreground text-xs">
                        {hasAnyCredits
                          ? `You can generate up to ${Math.max(1, maxAiVariations)} variation${Math.max(1, maxAiVariations) === 1 ? "" : "s"} with your current balance.`
                          : "You are out of image credits—top up to generate more variations."}
                      </p>
                      {aiImageFormErrors.count && (
                        <p className="text-destructive text-sm" role="alert">
                          {aiImageFormErrors.count}
                        </p>
                      )}
                      {requestedExceedsCredits && (
                        <p className="text-destructive text-sm" role="alert">
                          You only have {availableAiCredits} credit
                          {availableAiCredits === 1 ? "" : "s"} available.
                        </p>
                      )}
                    </div>

                    <div className="flex items-start gap-2">
                      <Checkbox
                        id="include-screenshot"
                        checked={!!aiImageFormData.include_screenshot}
                        onCheckedChange={(checked) => setAiImageFormData("include_screenshot", Boolean(checked))}
                      />
                      <div className="space-y-1">
                        <Label htmlFor="include-screenshot">Include webpage screenshot</Label>
                        <p className="text-muted-foreground text-sm">
                          This usually helps produce better results when the active image is not great.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        type="submit"
                        disabled={
                          processingAiImageFormData || isSubmitting || !hasAnyCredits || requestedExceedsCredits
                        }
                      >
                        {(processingAiImageFormData || isSubmitting) && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Generate {aiImageFormData.count} Variation
                        {aiImageFormData.count !== 1 ? "s" : ""}
                      </Button>
                      {!hasAnyCredits && !isTrial && (
                        <Button type="button" variant="outline" onClick={handleTopup}>
                          Buy credits
                        </Button>
                      )}
                      {isTrial && (
                        <Button type="button" variant="outline" onClick={() => router.visit("/subscribe")}>
                          Choose a plan
                        </Button>
                      )}
                    </div>
                  </form>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-1">
              <h2 className="text-xl font-semibold">Your future social preview images</h2>
              <p className="text-muted-foreground text-sm">
                We&apos;ll drop new images here as soon as they finish rendering. Hover to set one live.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">{gridItems.map((item) => renderGridItem(item))}</div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
